import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { HStack } from '../hstack'
import { WarningTriangleGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'
import { VStack } from '../vstack'

export type RestrictionCardProps = {
    /** Restriction headline: the public text (`message`), visible to all users. */
    title: string
    /** Translated "Internal note" section label. */
    internalNoteLabel?: string
    /** Internal note (`internalMessage`). Rendered only when the backend returned it. */
    internalNote?: string
    /** Translated "Date and time" label. */
    dateTimeLabel: string
    /** Formatted start–end range. */
    dateTimeValue: string
    className?: string
    testID?: string
}

const LabeledBlock: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <VStack className='gap-1'>
        <Text size='caption' tone='muted'>
            {label}
        </Text>
        <Text size='body' tone='default'>
            {value}
        </Text>
    </VStack>
)

/**
 * Stand/gate restriction card (Figma `Stands&GatesMap` / `Stands&GatesTimeline/RestrictionDetails`
 * / `Mobile/Stands&Gates`): a `bg/elevation` card with a `border/strong` hairline. Header is a
 * warning triangle + the restriction title; the body holds an optional internal-note block (shown
 * only when `internalNote` is passed) and the date-and-time range. Labels arrive already translated so
 * the component stays i18n-agnostic and cross-platform (web + mobile).
 */
export const RestrictionCard: React.FC<RestrictionCardProps> = ({
    title,
    internalNoteLabel,
    internalNote,
    dateTimeLabel,
    dateTimeValue,
    className,
    testID,
}) => (
    <Box
        testID={testID}
        className={twMerge(
            'gap-4 rounded border border-solid border-border-strong bg-background-elevation p-4',
            className,
        )}
    >
        <HStack className='items-start gap-2'>
            <WarningTriangleGlyph size={20} />
            <Text size='body-lg' weight='semiBold' tone='default' className='min-w-0 flex-1'>
                {title}
            </Text>
        </HStack>
        {internalNote ? (
            <LabeledBlock label={internalNoteLabel ?? ''} value={internalNote} />
        ) : null}
        <LabeledBlock label={dateTimeLabel} value={dateTimeValue} />
    </Box>
)
RestrictionCard.displayName = 'RestrictionCard'
